// Free Keto Hot Cocoa with a first subscription box (NIM-3508).
// Keeps exactly one gift Cocoa line (quantity 1, marked by the _krd_gift property) in the cart while the cart holds a
// 30- or 50-pack subscription line, and none otherwise. A Cocoa line without the property is the customer's own
// purchase and is never touched. Cart changes come from theme code and third-party apps (Recurpay), so the script
// watches every cart-mutating fetch/XHR instead of individual buttons, and also reconciles on page load.
(() => {
  if (window.krdGiftCocoa) return;

  const GIFT_VARIANT_ID = 67434388816030;
  const GIFT_PRODUCT_HANDLE = 'keto-hot-cocoa';
  const GIFT_PROPERTY = '_krd_gift';
  const QUALIFYING_VARIANT_IDS = [46804693581982, 46804694892702, 46804693614750, 46804694925470];
  const CART_MUTATION_PATH = /\/cart\/(add|change|update|clear)(\.js)?\/?$/;
  const DEBOUNCE_MS = 350;
  const MAX_PASSES = 3; // mutations per reconcile run
  const MAX_MUTATIONS_PER_MINUTE = 12; // hard stop against any unforeseen loop
  const UNAVAILABLE_KEY = 'krd_gift_cocoa_unavailable';
  const UNAVAILABLE_TTL_MS = 10 * 60 * 1000;
  const CHECKOUT_WAIT_MS = 2500; // longest a checkout click waits for the gift before it continues anyway
  const CHECKOUT_PATH = /\/checkouts?(\/|$)/;

  const nativeFetch = window.fetch.bind(window);
  const root = window.Shopify?.routes?.root || '/';
  // window.routes holds locale-aware paths without the .js suffix (e.g. /en-ca/cart/add).
  const endpoint = (name, fallback) => `${window.routes?.[name] || `${root}${fallback}`}.js`;

  let timer = null;
  let running = false;
  let dirty = false;
  let leaving = false;
  let checkingOut = false;
  let runPromise = null;
  const pendingMutations = new Set(); // cart requests from any code that have not answered yet
  let available = null; // null = unknown
  const mutationTimes = [];

  const isGift = (item) => item.variant_id === GIFT_VARIANT_ID && item.properties && item.properties[GIFT_PROPERTY];
  const isQualifying = (item) => item.selling_plan_allocation && QUALIFYING_VARIANT_IDS.includes(item.variant_id);

  function readUnavailableFlag() {
    try {
      const at = Number(sessionStorage.getItem(UNAVAILABLE_KEY));
      return at && Date.now() - at < UNAVAILABLE_TTL_MS;
    } catch (e) {
      return false;
    }
  }

  function markUnavailable() {
    available = false;
    try {
      sessionStorage.setItem(UNAVAILABLE_KEY, String(Date.now()));
    } catch (e) {}
  }

  async function giftAvailable() {
    if (available !== null) return available;
    if (readUnavailableFlag()) return (available = false);
    try {
      const response = await nativeFetch(`${root}products/${GIFT_PRODUCT_HANDLE}.js`, { headers: { Accept: 'application/json' } });
      if (!response.ok) return true; // unknown: let /cart/add decide
      const product = await response.json();
      const variant = (product.variants || []).find((v) => v.id === GIFT_VARIANT_ID);
      available = Boolean(variant && variant.available);
      if (!available) markUnavailable();
      return available;
    } catch (e) {
      return true;
    }
  }

  // Sections the theme itself re-renders after a quantity change (cart.js / cart-drawer.js).
  function sectionsToRender() {
    const sections = [];
    if (document.getElementById('CartDrawer')) sections.push({ id: 'CartDrawer', section: 'cart-drawer', selector: '.drawer__inner' });
    if (document.getElementById('cart-icon-bubble')) sections.push({ id: 'cart-icon-bubble', section: 'cart-icon-bubble', selector: '.shopify-section' });
    const mainItems = document.getElementById('main-cart-items');
    if (mainItems?.dataset.id) sections.push({ id: 'main-cart-items', section: mainItems.dataset.id, selector: '.js-contents' });
    const mainFooter = document.getElementById('main-cart-footer');
    if (mainFooter?.dataset.id) sections.push({ id: 'main-cart-footer', section: mainFooter.dataset.id, selector: '.js-contents' });
    if (document.getElementById('cart-live-region-text')) sections.push({ id: 'cart-live-region-text', section: 'cart-live-region-text', selector: '.shopify-section' });
    return sections;
  }

  function render(state, sections) {
    if (!state || !state.sections) return;
    const empty = state.item_count === 0;
    document.querySelector('cart-drawer')?.classList.toggle('is-empty', empty);
    document.querySelector('cart-drawer-items')?.classList.toggle('is-empty', empty);
    document.querySelector('cart-items')?.classList.toggle('is-empty', empty);
    document.getElementById('main-cart-footer')?.classList.toggle('is-empty', empty);
    sections.forEach(({ id, section, selector }) => {
      const html = state.sections[section];
      const target = document.getElementById(id);
      if (!html || !target) return;
      const source = new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
      if (!source) return;
      (target.querySelector(selector) || target).innerHTML = source.innerHTML;
    });
  }

  function budgetLeft() {
    const now = Date.now();
    while (mutationTimes.length && now - mutationTimes[0] > 60000) mutationTimes.shift();
    return mutationTimes.length < MAX_MUTATIONS_PER_MINUTE;
  }

  async function post(path, payload) {
    mutationTimes.push(Date.now());
    const sections = sectionsToRender();
    const body = JSON.stringify({ ...payload, sections: sections.map((s) => s.section), sections_url: window.location.pathname });
    const response = await nativeFetch(path, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body,
    });
    const state = await response.json().catch(() => null);
    return { response, state, sections };
  }

  // One step toward the desired cart. Returns true when it changed the cart.
  async function step() {
    const cart = await nativeFetch(endpoint('cart_url', 'cart'), { headers: { Accept: 'application/json' } }).then((r) => r.json());
    const gifts = cart.items.filter(isGift);
    const wanted = cart.items.some(isQualifying) && (await giftAvailable());

    if (wanted && gifts.length === 1 && gifts[0].quantity === 1) return false;
    if (!wanted && gifts.length === 0) return false;
    if ((leaving && !checkingOut) || !budgetLeft()) return false;

    if (wanted && gifts.length === 0) {
      const { response, state, sections } = await post(endpoint('cart_add_url', 'cart/add'), {
        items: [{ id: GIFT_VARIANT_ID, quantity: 1, properties: { [GIFT_PROPERTY]: 'Free with your first subscription box' } }],
      });
      if (!response.ok) {
        // 422 = sold out or not enough stock; any other refusal is treated the same: no gift, no retry for a while.
        markUnavailable();
        return false;
      }
      // /cart/add.js answers with the added items and the sections rendered after the add; the cart is not empty.
      render({ item_count: 1, sections: state?.sections }, sections);
      return true;
    }

    // Too many gifts, wrong quantity, or no longer wanted: fix by line key, never touching other lines.
    const updates = {};
    gifts.forEach((gift, index) => {
      updates[gift.key] = wanted && index === 0 ? 1 : 0;
    });
    const { response, state, sections } = await post(endpoint('cart_update_url', 'cart/update'), { updates });
    if (!response.ok) return false;
    render(state, sections);
    return true;
  }

  // Resolves when the cart has been brought to the desired state (including any rerun requested meanwhile).
  function reconcile() {
    if (running) {
      dirty = true;
      return runPromise;
    }
    running = true;
    runPromise = run();
    return runPromise;
  }

  async function run() {
    try {
      let passes = 0;
      do {
        dirty = false;
        // eslint-disable-next-line no-await-in-loop
        const changed = await step();
        if (changed) passes += 1;
        if (!changed && !dirty) break;
      } while (passes < MAX_PASSES);
    } catch (e) {
      // Network or parse failure: leave the cart as it is; the next cart change or page load reconciles again.
    } finally {
      running = false;
    }
  }

  function schedule() {
    clearTimeout(timer);
    timer = setTimeout(reconcile, DEBOUNCE_MS);
  }

  function isCartMutation(target) {
    try {
      return CART_MUTATION_PATH.test(new URL(target, window.location.href).pathname);
    } catch (e) {
      return false;
    }
  }

  function track(settled) {
    pendingMutations.add(settled);
    settled.then(() => {
      pendingMutations.delete(settled);
      schedule();
    });
  }

  window.fetch = function (input, init) {
    const promise = nativeFetch(input, init);
    const target = typeof input === 'string' ? input : input?.url || String(input);
    if (isCartMutation(target)) track(promise.then(() => {}, () => {}));
    return promise;
  };

  const nativeOpen = XMLHttpRequest.prototype.open;
  XMLHttpRequest.prototype.open = function (method, target, ...rest) {
    if (isCartMutation(String(target)) && !this.krdGiftWatched) {
      this.krdGiftWatched = true;
      track(new Promise((resolve) => this.addEventListener('loadend', resolve, { once: true })));
    }
    return nativeOpen.call(this, method, target, ...rest);
  };

  // Checkout: a subscription added a moment before the checkout click must still reach checkout with its gift.
  // Hold the navigation until pending cart requests have answered and the cart is reconciled, at most
  // CHECKOUT_WAIT_MS, then continue it exactly as the customer started it. Any failure continues at once.
  async function beforeCheckout() {
    clearTimeout(timer);
    checkingOut = true;
    try {
      await Promise.race([
        (async () => {
          await Promise.all([...pendingMutations]);
          if (runPromise) await runPromise;
          await reconcile();
        })(),
        new Promise((resolve) => setTimeout(resolve, CHECKOUT_WAIT_MS)),
      ]);
    } catch (e) {
      // Never block checkout.
    } finally {
      checkingOut = false;
    }
  }

  const releasedForms = new WeakSet();

  document.addEventListener(
    'submit',
    (event) => {
      const form = event.target;
      const submitter = event.submitter;
      if (!(form instanceof HTMLFormElement) || releasedForms.has(form)) {
        if (form instanceof HTMLFormElement) releasedForms.delete(form);
        return;
      }
      const toCheckout = submitter?.name === 'checkout' || CHECKOUT_PATH.test(form.getAttribute('action') || '');
      if (!toCheckout) return;
      event.preventDefault();
      event.stopImmediatePropagation();
      beforeCheckout().finally(() => {
        // The re-render may have replaced the form and its button; continue with the live ones.
        const live = (el) => el && (el.isConnected ? el : (el.id && document.getElementById(el.id)) || null);
        const liveForm = live(form);
        const liveSubmitter = live(submitter);
        if (!liveForm) {
          window.location.assign(`${root}checkout`);
          return;
        }
        releasedForms.add(liveForm);
        if (liveForm.requestSubmit && liveSubmitter && liveSubmitter.form === liveForm) {
          liveForm.requestSubmit(liveSubmitter);
          return;
        }
        if (submitter?.name) {
          const input = document.createElement('input');
          input.type = 'hidden';
          input.name = submitter.name;
          input.value = submitter.value;
          liveForm.appendChild(input);
        }
        if (liveForm.requestSubmit) liveForm.requestSubmit();
        else liveForm.submit();
      });
    },
    true
  );

  document.addEventListener(
    'click',
    (event) => {
      if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
      const link = event.target.closest?.('a[href]');
      if (!link || (link.target && link.target !== '_self')) return;
      let href;
      try {
        href = new URL(link.href, window.location.href);
      } catch (e) {
        return;
      }
      if (href.origin !== window.location.origin || !CHECKOUT_PATH.test(href.pathname)) return;
      event.preventDefault();
      beforeCheckout().finally(() => {
        window.location.assign(href.href);
      });
    },
    true
  );

  // A redirect right after an add (e.g. the homepage offer goes to /cart) must not race the next page's reconcile.
  window.addEventListener('beforeunload', () => {
    leaving = true;
    setTimeout(() => {
      leaving = false;
    }, 5000);
  });
  window.addEventListener('pageshow', (event) => {
    if (event.persisted) {
      leaving = false;
      schedule();
    }
  });

  window.krdGiftCocoa = { reconcile };
  // No cart cookie means this visitor has never had a cart: nothing to reconcile until their first cart request.
  if (/(^|;\s*)cart=/.test(document.cookie)) reconcile();
})();
